// Views
import TableView from '../../../views/Table';

// Components
import DashboardContent from '../../organisms/DashboardContent/DashboardContent';
import { Skeleton } from '../../atoms/Skeleton/Skeleton';

// Styles
import styles from './TabContentRenderer.module.css';
import Timeline from '../../../views/Timeline';

/**
 * @param {string | null} type
 * @returns {boolean}
 */
const isCalendarTabType = (type) => (
  type === 'planning' || type === 'planning_vertical' || type === 'timeline'
);

/**
 * @param {string | null} type
 * @returns {string}
 */
const getWrapperClassName = (type) => {
  if (type === 'dashboard') {
    return styles['tab-content-renderer-dashboard'];
  }
  if (isCalendarTabType(type)) {
    return styles['tab-content-renderer-timeline'];
  }
  return styles['tab-content-renderer-table'];
};

/**
 * @param {object} props
 * @param {string | null} props.type
 * @returns {JSX.Element}
 */
function TabContentSkeleton({ type }) {
  return (
    <div className={getWrapperClassName(type)}>
      <div className={styles['tab-content-renderer-body']}>
        <div className={styles.skeleton}>
          <Skeleton shape="rectangle" width="100%" height="48px" />
          <Skeleton shape="rectangle" width="100%" height="200px" />
          <Skeleton shape="rectangle" width="80%" height="24px" />
        </div>
      </div>
    </div>
  );
}

/**
 * TabContentRenderer - Renders appropriate content based on tab format type
 * Each component (TableView, Timeline, DashboardContent) handles its own data fetching and loading state
 * @param {object} props
 * @param {string | null} props.type - Tab format type (table, dashboard, planning, timeline)
 * @param {ApiPage} [props.page] - Page configuration with path (needed for table and dashboard)
 * @param {boolean} [props.isLoading=false] - Loading state to display content skeleton
 * @returns {JSX.Element | null} TabContentRenderer component
 */
function TabContentRenderer({
  type,
  page,
  isLoading = false,
}) {
  if (!type) return null;

  if (isLoading) {
    return <TabContentSkeleton type={type} />;
  }

  switch (type) {
    case 'table': {
      return (
        <div className={styles['tab-content-renderer-table']}>
          <div className={styles['tab-content-renderer-body']}>
            <TableView
              stickyTopElems={['page-header', 'horizontal-tabs', 'banner-header-action-row']}
              isInTab
              page={page || {
                path: 'soiree/liste_soirees.php',
                template: 'table',
                format: null,
              }}
            />
          </div>
        </div>
      );
    }

    case 'planning':
    case 'planning_vertical':
    case 'timeline':
      return (
        <div className={styles['tab-content-renderer-timeline']}>
          <div className={styles['tab-content-renderer-body']}>
            <Timeline page={page} isInTab />
          </div>
        </div>
      );

    case 'dashboard':
      return (
        <div className={styles['tab-content-renderer-dashboard']}>
          <div className={styles['tab-content-renderer-body']}>
            <DashboardContent
              page={page || {
                path: '',
                template: 'dashboard',
                format: null,
              }}
            />
          </div>
        </div>
      );

    default:
      return (
        null
      );
  }
}

export default TabContentRenderer;
